import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import {
  Application,
  ApplicationAnswers,
  Field,
  FieldType,
  FieldValidation,
  Range,
  SpecialFieldKeys,
  TextInputType,
} from '@bybeam/platform-types';
import {
  FieldCondition,
  FieldValidator,
  GenericFormData,
  SimpleValidationFieldConfig,
  ValidationConfig,
  ValidationFieldConfig,
  checkboxFieldIsPresent,
  conditionallyRequiredField,
  dateValidation,
  defaultRequiredErrorMessage,
  emailValidation,
  numberValidation,
  optionalField,
  phoneValidation,
  requiredField,
} from '@platform-ui-common/hooks/useForm/index.js';
import { getUnhousedKey } from '@platform-ui-common/utilities/address.js';
import { formatCurrency, formatNumber } from '@platform-ui-common/utilities/format.js';
import { evaluate } from '../ast/evaluate.js';
import { getFieldLabel } from './index.js';
import { getFieldInitialState } from './value.js';

function getRequiredErrorMessage(field: Field): string | undefined {
  switch (field.type) {
    case FieldType.Date:
    case FieldType.Text:
      return defaultRequiredErrorMessage(field.copy);
    case FieldType.Document:
      return 'At least one document is required';
    case FieldType.Checkbox:
      return 'This checkbox is required';
    case FieldType.Dropdown:
    case FieldType.CheckboxGroup:
    case FieldType.RadioList:
      return defaultRequiredErrorMessage('A selection');
    default:
      return undefined;
  }
}

function getFieldValidation(field: Field): FieldValidation | undefined {
  switch (field.type) {
    case FieldType.Calculated:
      return field.display?.validation;
    default:
      return 'validation' in field ? field.validation : undefined;
  }
}

function isRequired(field: Field): boolean {
  const validation = getFieldValidation(field);
  return validation?.required ?? true;
}

function getAddressValidationConfig<FormData extends GenericFormData>(
  field,
): ValidationConfig<FormData> {
  const { key, allowUnhoused } = field;
  const required = isRequired(field);

  const unhousedKey = getUnhousedKey(key);
  let addressLine1: SimpleValidationFieldConfig<FormData>;
  if (!required) {
    addressLine1 = optionalField();
  } else {
    addressLine1 = allowUnhoused
      ? conditionallyRequiredField(
          undefined,
          ({ [unhousedKey]: isUnhoused }) => !isUnhoused,
          defaultRequiredErrorMessage('Street Address'),
        )
      : requiredField(undefined, defaultRequiredErrorMessage('Street Address'));
  }

  return {
    [`${key}.addressLine1`]: addressLine1,
    [`${key}.addressLine2`]: optionalField(),
    [`${key}.city`]: required
      ? requiredField(undefined, defaultRequiredErrorMessage('City'))
      : optionalField(),
    [`${key}.state`]: required
      ? requiredField(undefined, defaultRequiredErrorMessage('State'))
      : optionalField(),
    [`${key}.zip`]: required
      ? requiredField(undefined, defaultRequiredErrorMessage('Zip Code'))
      : optionalField(),
    ...(field.copyAddressKey && { [`${key}.copied`]: optionalField() }),
    ...(allowUnhoused && { [unhousedKey]: optionalField() }),
  } as ValidationConfig<FormData>;
}

// Returns the parameters for the `useForm` hook initialization for the given field.
// Initializes the data with the current value of the field (or the expected default
// if the answers do not contain a value for the field), and returns any expected
// validation.
export function getFieldFormInit<FormData extends GenericFormData>(
  field: Field,
  answers: Partial<ApplicationAnswers>,
  application: Application,
): [FormData, ValidationConfig<FormData>] {
  return [
    getFieldInitialState(field, answers, application),
    getFieldValidationConfig(field, application),
  ];
}

function getFieldCondition<FormData extends GenericFormData>(
  field: Field,
): FieldCondition<FormData> {
  const validation = getFieldValidation(field);
  const conditions = [field.dynamicLogic, validation?.condition].filter(Boolean);
  if (conditions.length === 0) return undefined;
  return (data) => conditions.every((condition) => evaluate(condition, data));
}

function rangeValidation<FormData extends GenericFormData>(
  field: Field,
  range: Range,
  message?: string,
): FieldValidator<FormData> {
  const { min, max } = range;
  const fieldName = getFieldLabel(field) ?? 'Value';
  let defaultMessage: string;
  if (!range.max) defaultMessage = `${fieldName} must be at least ${formatNumber(min)}`;
  else if (!range.min) defaultMessage = `${fieldName} must be no more than ${formatNumber(max)}`;
  else
    defaultMessage = `${fieldName} must be between ${formatNumber(min)} and ${formatNumber(max)}`;

  return ({ [field.key]: value }) => {
    if (numberValidation(value)) return `${fieldName} must be a number`;

    const parsedValue = typeof value === 'string' ? Number(value) : value;
    return (
      (parsedValue < (min ?? Number.NEGATIVE_INFINITY) ||
        parsedValue > (max ?? Number.POSITIVE_INFINITY)) &&
      (message ?? defaultMessage)
    );
  };
}

function getFieldSpecificValidationRules<FormData extends GenericFormData>(
  field: Field,
  application: Application,
): FieldValidator<FormData> {
  switch (field.type) {
    case FieldType.Date:
      return dateValidation(
        field.key,
        {
          allowPastDates:
            !field.validation?.allowedRange || field.validation.allowedRange === 'past',
          allowFutureDates:
            !field.validation?.allowedRange || field.validation.allowedRange === 'future',
        },
        field.copy,
      );
    case FieldType.Text:
      switch (field.key) {
        case SpecialFieldKeys.RequestedAmount:
          return rangeValidation(
            field,
            { min: 1, max: application?.case?.program?.config?.maxFundingAmount / 100 },
            `Requested amount cannot be higher than ${formatCurrency(
              application?.case?.program?.config?.maxFundingAmount,
              false,
            )}`,
          );
      }

      switch (field.inputType) {
        case TextInputType.Email:
          return emailValidation(field.key);
        case TextInputType.Phone:
          return phoneValidation(field.key);
        case TextInputType.Currency:
        case TextInputType.Number:
          return ({ [field.key]: value }) =>
            numberValidation(value) && `${field.copy} cannot be lower than 0`;
      }
  }
}

function getFieldValidate<FormData extends GenericFormData>(
  field: Field,
  application: Application,
): FieldValidator<FormData> {
  const validation = getFieldValidation(field);

  const validators = [
    getFieldSpecificValidationRules(field, application),
    ...(validation?.rules ?? []).map(
      ({ rule, message }) =>
        (formData) =>
          !evaluate(rule, formData) && message,
    ),
  ].filter(Boolean);
  if (validators.length === 0) return undefined;

  return (formData) => {
    const errors = validators
      .map((validate) => validate(formData))
      .filter(Boolean)
      .flat();
    if (errors.length === 0) return undefined;
    if (errors.length === 1) return errors[0];
    return errors as string[];
  };
}

export function getFieldValidationConfig<FormData extends GenericFormData>(
  field: Field,
  application: Application,
): ValidationConfig<FormData> {
  const required = isRequired(field);
  const condition = getFieldCondition(field);
  const requiredErrorMessage = getRequiredErrorMessage(field);
  const validate = getFieldValidate(field, application);

  switch (field.type) {
    case FieldType.Address:
      return getAddressValidationConfig(field);
    case FieldType.Complex:
      return {
        [field.key]: {
          type: 'repeated',
          required,
          condition,
          schema: Object.fromEntries(
            field.subFields.flatMap((subField) =>
              Object.entries(getFieldValidationConfig(subField, application)),
            ),
          ),
        },
      } as ValidationConfig<FormData>;
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentionally fall through for display
    case FieldType.Calculated:
      if (!field.display) return {};
    // Fall through to next case
    case FieldType.Checkbox:
    case FieldType.CheckboxGroup:
    case FieldType.Date:
    case FieldType.Document:
    case FieldType.Dropdown:
    case FieldType.RadioList:
    case FieldType.Text:
      return {
        [field.key]: {
          type: 'simple',
          required,
          requiredErrorMessage,
          condition,
          validate,
          fieldIsPresent:
            field.type === FieldType.Checkbox ? checkboxFieldIsPresent(field.key) : undefined,
        },
      } as ValidationConfig<FormData>;
    case FieldType.Typography:
      return {};
    default:
      return assertExhaustive(field);
  }
}

export function extractFieldValidation<FormData extends GenericFormData>(
  field: Field,
  config: ValidationConfig<FormData> = {},
): ValidationFieldConfig<FormData> {
  switch (field.type) {
    case FieldType.Address:
      return config[[field.key, 'addressLine1'].join('.')];
    default:
      return config[field.key];
  }
}
